import {
  Box,
  Typography,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import { FileText, Building2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import QuoteList from "./components/QuoteList";
import QuoteForm from "./components/QuoteForm";
import ClientList from "./components/ClientList";
import ClientDetail from "./components/ClientDetail";

const SECTION_KEYS = [
  { key: "devis", label: "Devis", icon: FileText },
  { key: "clients", label: "Clients", icon: Building2 },
];

const FacturationScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const pathParts = location.pathname
    .replace(/^\/admin\/facturation\/?/, "")
    .split("/")
    .filter(Boolean);
  const activeSection = SECTION_KEYS.find((s) => s.key === pathParts[0])?.key || "devis";
  const subId = pathParts[1] || null;

  const handleSectionChange = (key) => {
    navigate(`/admin/facturation/${key}`);
  };

  const renderContent = () => {
    switch (activeSection) {
      case "devis":
        if (subId === "new") return <QuoteForm />;
        if (subId) return <QuoteForm quoteId={subId} />;
        return <QuoteList />;
      case "clients":
        if (subId === "new") return <ClientDetail />;
        if (subId) return <ClientDetail orgId={subId} />;
        return <ClientList />;
      default:
        return <QuoteList />;
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 400,
            fontSize: "1.5rem",
            color: theme.palette.text.primary,
            mb: 0.5,
            fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}
        >
          Facturation
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Gestion des devis et clients
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 0.75,
          mb: 3,
          flexWrap: "wrap",
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {SECTION_KEYS.map(({ key, label, icon: Icon }) => {
          const isActive = activeSection === key;
          return (
            <Chip
              key={key}
              icon={<Icon size={14} strokeWidth={isActive ? 2 : 1.5} />}
              label={label}
              onClick={() => handleSectionChange(key)}
              variant={isActive ? "filled" : "outlined"}
              size="small"
              sx={{
                borderRadius: "8px",
                fontWeight: isActive ? 600 : 500,
                fontSize: "0.8rem",
                height: 32,
                transition: "all 0.2s",
                bgcolor: isActive ? alpha(theme.palette.primary.main, 0.12) : "transparent",
                color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                borderColor: isActive
                  ? alpha(theme.palette.primary.main, 0.3)
                  : theme.palette.divider,
                "& .MuiChip-icon": {
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                },
                "&:hover": {
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, 0.15)
                    : alpha(theme.palette.divider, 0.5),
                  color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                },
              }}
            />
          );
        })}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>{renderContent()}</Box>
    </Box>
  );
};

export default FacturationScreen;
